import type { DateTime } from "luxon";
import { useCallback } from "react";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useDayWeek } from "../../contexts/WeekView/useDayWeek";
import { useWeekViewContext } from "../../contexts/WeekView/WeekViewContext";
import { useNurseTimeOff } from "../../hooks/useNurseTimeOff";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { findScheduleSlot } from "../../utils/departmentWeeks";
import type { Nurse, NurseInShift } from "../../utils/models";
import type { ShiftWindow } from "../../utils/scheduleConflict";
import { useStaffingTargetCheck } from "../../utils/warningDialogs/useStaffingTargetCheck";
import { type NurseWarningState, useNurseWarnings, type WarningDialogProps } from "../../utils/warningDialogs/WarningDialog";
import { quickAddPersonWarningInputs } from "./quickAddWarningInputs";

// The confirm/cancel handlers `useNurseWarnings` returns after its state and `checkWarnings`, in its order.
type Handlers = ReturnType<typeof useNurseWarnings> extends [unknown, unknown, ...infer Rest] ? Rest : never;

/** The cascade's state and handlers, laid out as `WarningDialog`'s props. */
const toWarningDialogProps = (state: NurseWarningState, handlers: Handlers, longShiftDuration: number | undefined): WarningDialogProps => {
	const [onConfirmAgency, onConfirmOvertime, onConfirmDailyHours, onCancel, onConfirmPto, onConfirmTarget, onConfirmEmployeeStatus] = handlers;
	return {
		openPtoWarning: state.isPtoWarningOpen,
		openTargetWarning: state.isTargetWarningOpen,
		openAgencyWarning: state.isAgencyWarningOpen,
		openOvertimeWarning: state.isOvertimeWarningOpen,
		openDailyHoursWarning: state.isDailyHoursWarningOpen,
		openEmployeeStatusWarning: state.isEmployeeStatusWarningOpen,
		overtimeDetails: state.overtimeDetails,
		dailyHoursDetails: state.dailyHoursDetails,
		targetDetails: state.targetDetails,
		employeeStatusDetails: state.employeeStatusDetails,
		longShiftDuration,
		...{
			onCancelPto: onCancel,
			onCancelTarget: onCancel,
			onCancelAgency: onCancel,
			onCancelOvertime: onCancel,
			onCancelDailyHours: onCancel,
			onCancelEmployeeStatus: onCancel,
		},
		...{ onConfirmPto, onConfirmTarget, onConfirmAgency, onConfirmOvertime, onConfirmDailyHours, onConfirmEmployeeStatus },
	};
};

type Params = {
	nurse: Nurse | null;
	location: string | null;
	window: ShiftWindow;
	movingShift: NurseInShift | undefined;
	date: DateTime;
	showActual: boolean;
	onComplete: () => void;
};

/**
 * The "+" dialog's pre-save checks: the destination's staffing target always, then the person's own (see
 * `quickAddPersonWarningInputs`), and `onComplete` once every warning shown was confirmed.
 */
export const useQuickAddWarnings = ({ nurse, location, window, movingShift, date, showActual, onComplete }: Params) => {
	const { chosenDepartment } = useHospitalData();
	const { scheduleWeeks } = useWeekViewContext();
	const { dayWeek, shiftTypeOfTimes } = useDayWeek(date);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const getTargetCheck = useStaffingTargetCheck();
	const timeOffOf = useNurseTimeOff();
	const [warningState, checkWarnings, ...handlers] = useNurseWarnings(onComplete, chosenDepartment.longShiftDuration);

	const handleSave = useCallback(() => {
		if (!nurse) throw new Error("useQuickAddWarnings: save reached with no person picked");
		// The daily-hours count matches every row on the calendar weekday, not one row's own position.
		const person = quickAddPersonWarningInputs({
			nurse,
			timeOff: timeOffOf(nurse.id),
			department: chosenDepartment,
			weeklyShifts: dayWeek().week.weeklyShifts,
			dayIndex: date.weekdayISO,
			window,
			movingShift,
			jobManagementEnabled,
		});
		// The shift type has no row on a day nobody works it, so there's no target to pass.
		const slot = findScheduleSlot(scheduleWeeks, date, shiftTypeOfTimes(window));
		const targetWarning =
			slot &&
			getTargetCheck({
				weeks: scheduleWeeks,
				locationName: location,
				weekIndex: slot.weekIndex,
				dayNo: slot.dayIndex,
				shiftIndex: slot.shiftIndex,
				isActual: showActual,
			});
		checkWarnings(person.isAgency, undefined, undefined, person.dailyHours, showActual, person.hasPto, targetWarning, person.employeeStatusWarning);
	}, [
		nurse,
		timeOffOf,
		scheduleWeeks,
		dayWeek,
		shiftTypeOfTimes,
		date,
		chosenDepartment,
		window,
		movingShift,
		jobManagementEnabled,
		getTargetCheck,
		location,
		showActual,
		checkWarnings,
	]);

	return { handleSave, warningDialogProps: toWarningDialogProps(warningState, handlers, chosenDepartment.longShiftDuration) };
};
